<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMessages } from '@composables/use-messages'
import { useFavouriteToggle } from '@composables/use-favourite-toggle'

const props = defineProps({
    show: {
        type: Object,
        default: () => {},
    },
    hasViewShowOption: {
        type: Boolean,
        default: true,
    },
})

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()
const favourites = useFavouriteToggle()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.show.id) return ''

    const href = router.resolve({ name: 'show', params: { id: props.show.id } }).href
    return siteUrl(href)
})

const isFollowing = computed(() => Boolean(props.show.id) && favourites.isFavourite({ type: 'shows', id: props.show.id }))

const header = computed(() => ({
    image: props.show,
    title: props.show.title,
    subtitle: 'Show',
}))

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// A row whose entity has no id is hidden: the View row's router-link would throw resolving
// a missing param, and master's follow button went inert (pointer-events: none) for it.
const items = computed(() => [
    { id: 'view', label: 'View Show', to: { name: 'show', params: { id: props.show.id } }, hidden: !props.hasViewShowOption || !props.show.id },
    { divider: true },
    {
        id: 'follow-show',
        label: isFollowing.value ? 'Following' : 'Follow Show',
        hidden: !props.show.id,
        action: () => favourites.toggle({ type: 'shows', id: props.show.id, addedKey: 'FAV_SHOW_ADDED', removedKey: 'FAV_SHOW_REMOVED' }),
    },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.show.title} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: 'Copy Show Link', action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="show-options"
        :items="items"
        :header="header"
        trigger-label="Show options"
    >
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>
